import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import { InputAdornment, Icon, CircularProgress } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "pages/authentication/components/BasicLayout";
import colors from "assets/theme/base/colors";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import { forgetPassword } from "actions/password";
import { REGEX } from "components/common/constant";

const initialFields = {
  email: { value: "", error: "" },
};

export const ForgetPassword = () => {
  const { notify } = useNotify();
  const navigate = useNavigate();
  const [forgotPasswordInfo, setForgotPasswordInfo] = useState(initialFields);
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setForgotPasswordInfo((prev) => ({
      ...prev,
      [field]: { ...prev[field], value, error: "" },
    }));
  };

  const getValidationError = (field, value) => {
    const trimmedValue = value.trim();

    switch (field) {
      case "email":
        if (!trimmedValue) return "Please enter email";
        if (!REGEX.EMAIL_REGEX.test(trimmedValue))
          return "Please enter a valid email address";
        return "";
      default:
        return "";
    }
  };

  const handleBlur = (field) => {
    const value = forgotPasswordInfo[field].value.trim();
    const error = getValidationError(field, value);
    setForgotPasswordInfo((prev) => ({
      ...prev,
      [field]: { ...prev[field], error, value },
    }));
  };

  const validateForm = () => {
    let newData = { ...forgotPasswordInfo };

    Object.keys(newData).forEach((key) => {
      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });

    setForgotPasswordInfo(newData);
    return Object.values(newData).every((item) => item.error === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      const emailData = {
        email: forgotPasswordInfo.email.value,
      };

      const res = await forgetPassword(emailData);
      if (res?.code === 200) {
        notify(res?.message || "Recovery link sent to your email", "success");
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
            .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to send recovery link";

        notify(message, "error");
      }
    } catch (error) {
      notify(
        error?.message,
        "Failed to send recovery link to your email",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasicLayout
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        pt: 6,
        background: "linear-gradient(180deg, #f5f6fa 0%, #e8edf7 100%)",
      }}
    >
      <Card
        sx={{ maxWidth: 500, width: "100%", borderRadius: 4, boxShadow: 15 }}
      >
        <MDBox textAlign="center" p={4}>
          <MDTypography variant="h4" fontWeight="bold" color="dark">
            Forgot Password
          </MDTypography>

          <MDTypography variant="body2" color="text.secondary" mt={1}>
            Enter your email to receive recovery link
          </MDTypography>
        </MDBox>

        <MDBox
          pt={3}
          pb={4}
          px={4}
          component="form"
          role="form"
          onSubmit={handleSubmit}
        >
          <MDBox mb={3}>
            <RequiredLabel>Email</RequiredLabel>
            <MDInput
              type="email"
              placeholder="Enter your email"
              fullWidth
              value={forgotPasswordInfo.email.value}
              onChange={(e) => handleChange("email", e.target.value)}
              onBlur={() => handleBlur("email")}
              error={!!forgotPasswordInfo.email.error}
              // helperText={forgotPasswordInfo.email.error}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon fontSize="small" sx={{ color: colors.deepBlue.main }}>
                      email
                    </Icon>
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 },
              }}
            />
            {forgotPasswordInfo.email.error && (
              <MDTypography variant="caption" color="error">
                {forgotPasswordInfo.email.error}
              </MDTypography>
            )}
          </MDBox>

          <MDBox mt={2} mb={3}>
            <MDButton
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              color="deepBlue"
              sx={{
                py: 1.75,
                fontWeight: "bold",
                fontSize: "0.95rem",
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Send Recovery Email"
              )}
            </MDButton>
          </MDBox>

          <MDBox textAlign="center">
            <MDTypography
              variant="body2"
              sx={{
                cursor: "pointer",
                color: colors.deepBlue.main,
              }}
              onClick={() => navigate("/auth/sign-in")}
            >
              Back to Login
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
};
